import React from 'react';
import { PencilSimple, Power, Prohibit, SpinnerGap, Trash } from '@phosphor-icons/react';
import { typInfo } from '../../../../lib/zaehler/typen';
import { kurzDatum, standText } from '../../../../lib/zaehler/liste';
import type { AblesungZeile, ZaehlerDaten } from '../../../../lib/zaehler/useZaehlerDetail';

const KNOPF: React.CSSProperties = {
  height: 44, borderRadius: 14, border: 'none', gap: 7, fontSize: 13, fontWeight: 800, fontFamily: 'inherit',
  background: 'var(--acc-ink-fill)', color: 'var(--acc-ink)',
};

/**
 * Kopfkarte (Handoff Zähler §5.3): Akzentfläche, Typ-Icon, Chips (Typ, Aktiv-Schalter), Nummer, Ort,
 * letzter Stand; Bearbeiten und Löschen mit Rückfrage „Ja“/„Nein“ direkt in der Karte.
 */
export function KopfKarte({ z, letzte, telefon, mobil, schaltet, loeschFrage, loescht, onAktiv, onBearbeiten, onLoeschFrage, onLoeschen }: {
  z: ZaehlerDaten;
  letzte: AblesungZeile | null;
  telefon: boolean;
  /** Mobilmodus: Klickflächen mindestens 44 px */
  mobil: boolean;
  schaltet: boolean;
  loeschFrage: boolean;
  loescht: boolean;
  onAktiv: () => void;
  onBearbeiten: () => void;
  onLoeschFrage: (an: boolean) => void;
  onLoeschen: () => void;
}) {
  const t = typInfo(z.typ);
  const IconKomp = t.icon;
  const ort = [z.objekt?.objektnummer, z.objekt?.bezeichnung, z.einheit_bezeichnung, z.standort].filter(Boolean).join(' · ');
  const chip: React.CSSProperties = { height: 24, padding: '0 10px', borderRadius: 12, fontSize: 11, fontWeight: 800, background: 'var(--acc-ink-fill)', color: 'var(--acc-ink)' };
  return (
    <section className="immo-akzent flex flex-col" aria-label={`Zähler ${z.zaehler_nummer}`}
      style={{ borderRadius: 28, padding: telefon ? 18 : '22px 24px', gap: 18, background: 'var(--acc-fill)', color: 'var(--acc-ink)', boxShadow: 'var(--acc-sh)' }}>
      <div className="flex flex-wrap items-start" style={{ gap: 18 }}>
        <span className="flex items-center justify-center shrink-0" aria-hidden style={{ width: 64, height: 64, borderRadius: 20, background: 'var(--acc-ink-fill)' }}>
          <IconKomp weight="fill" size={30} />
        </span>
        <div className="flex flex-col min-w-0" style={{ flex: '1 1 240px', gap: 6 }}>
          <div className="flex flex-wrap items-center" style={{ gap: 6 }}>
            <span className="inline-flex items-center" style={chip}>{t.label}</span>
            {/* mobil 44 px Klickfläche um den 24-px-Chip, ohne die Zeile höher zu machen */}
            <button type="button" onClick={onAktiv} disabled={schaltet} aria-pressed={z.aktiv} title={z.aktiv ? 'Zähler deaktivieren' : 'Zähler aktivieren'}
              className="inline-flex items-center cursor-pointer"
              style={{ height: mobil ? 44 : 24, margin: mobil ? '-10px 0' : 0, padding: 0, border: 'none', background: 'transparent', color: 'inherit', fontFamily: 'inherit' }}>
              <span className="inline-flex items-center" style={{ ...chip, gap: 5 }}>
                {schaltet ? <SpinnerGap size={12} className="animate-spin" /> : z.aktiv ? <Power size={12} weight="bold" /> : <Prohibit size={12} weight="bold" />}
                {z.aktiv ? 'Aktiv' : 'Inaktiv'}
              </span>
            </button>
          </div>
          <h1 className="m-0 immo-zahl" style={{ fontSize: telefon ? 22 : 30, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.1, overflowWrap: 'anywhere' }}>
            {z.zaehler_nummer}
          </h1>
          {ort && <span style={{ fontSize: 13, color: 'var(--acc-ink2)' }}>{ort}</span>}
        </div>
        {letzte && (
          <div className="flex flex-col" style={{ marginLeft: telefon ? 0 : 'auto', textAlign: telefon ? 'left' : 'right', gap: 2, flex: telefon ? '1 1 100%' : '0 0 auto' }}>
            <span style={{ fontSize: 12, fontWeight: 700 }}>Letzter Stand</span>
            <span className="immo-zahl" style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.02em', whiteSpace: 'nowrap' }}>{standText(letzte.wert, z.einheit)}</span>
            <span className="immo-zahl" style={{ fontSize: 12, color: 'var(--acc-ink2)' }}>{kurzDatum(letzte.datum)}</span>
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center" style={{ gap: 8 }}>
        <button type="button" onClick={onBearbeiten} className="inline-flex items-center cursor-pointer" style={{ ...KNOPF, padding: '0 18px' }}>
          <PencilSimple size={14} weight="bold" />Bearbeiten
        </button>
        {loeschFrage ? (
          <>
            <button type="button" onClick={onLoeschen} disabled={loescht} className="inline-flex items-center cursor-pointer"
              style={{ ...KNOPF, padding: '0 18px', background: 'var(--bad)', color: '#fff', fontWeight: 700 }}>
              {loescht ? <SpinnerGap size={14} className="animate-spin" /> : <Trash size={14} weight="bold" />}Ja
            </button>
            <button type="button" onClick={() => onLoeschFrage(false)} disabled={loescht} className="inline-flex items-center cursor-pointer"
              style={{ ...KNOPF, padding: '0 18px', fontWeight: 700 }} autoFocus>
              Nein
            </button>
          </>
        ) : (
          <button type="button" onClick={() => onLoeschFrage(true)} title="Löschen" aria-label="Zähler löschen"
            className="inline-flex items-center justify-center cursor-pointer" style={{ ...KNOPF, width: 48 }}>
            <Trash size={16} weight="bold" />
          </button>
        )}
      </div>
    </section>
  );
}
